// src/components/billing/OutletSelectionModal.tsx
import React, { useState, useEffect } from 'react';

export interface SelectableOutlet {
  id: number;
  name: string;
}

interface Props {
  moduleName: string;
  outlets: SelectableOutlet[];
  cap: number;
  currentActiveIds: number[];
  onConfirm: (outletIds: number[]) => Promise<void>;
}

export default function OutletSelectionModal({ moduleName, outlets, cap, currentActiveIds, onConfirm }: Props) {
  const [selected, setSelected] = useState<Set<number>>(new Set(currentActiveIds));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const seeded = new Set(currentActiveIds);
    if (seeded.size > cap) {
      setSelected(new Set(Array.from(seeded).slice(0, cap)));
    } else {
      setSelected(seeded);
    }
  }, [cap]);

  const toggle = (id: number) => {
    setError(null);
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        return next;
      }
      if (next.size >= cap) {
        setError(`You can only select ${cap} outlet${cap !== 1 ? 's' : ''} on your current plan.`);
        return prev;
      }
      next.add(id);
      return next;
    });
  };

  const handleConfirm = async () => {
    if (selected.size === 0) {
      setError('Select at least one outlet.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onConfirm(Array.from(selected));
    } catch (e: any) {
      setError(e.message || 'Could not save your selection. Try again.');
    } finally {
      setSaving(false);
    }
  };

  const atCap = selected.size >= cap;

  if (cap <= 0) {
    return (
      <div style={{
        position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(15,23,42,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <div style={{
          background: '#fff', borderRadius: 20, padding: 24, width: '100%', maxWidth: 440,
          boxShadow: '0 24px 80px rgba(0,0,0,0.2)', textAlign: 'center',
          fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        }}>
          <h3 style={{ fontSize: 17, fontWeight: 800, color: '#0f172a', margin: '0 0 6px' }}>
            {moduleName} plan issue
          </h3>
          <p style={{ fontSize: 12, color: '#64748b', lineHeight: 1.5, margin: '0 0 16px' }}>
            Your {moduleName} plan currently shows 0 allowed outlets, which looks like a billing data error rather than something you can fix here.
            Please contact support so we can correct your subscription's outlet allowance.
          </p>
        </div>
      </div>
    );
  }

 return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(15,23,42,0.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        background: '#fff', borderRadius: 20, padding: 24, width: '100%', maxWidth: 440,
        maxHeight: '85vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 24px 80px rgba(0,0,0,0.2)',
        fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      }}>
        <h3 style={{ fontSize: 17, fontWeight: 800, color: '#0f172a', margin: '0 0 6px' }}>
          Choose your active outlets
        </h3>
        <p style={{ fontSize: 12, color: '#64748b', lineHeight: 1.5, margin: '0 0 16px' }}>
          Your {moduleName} plan covers {cap} outlet{cap !== 1 ? 's' : ''}, but it's assigned to {outlets.length}.
          Pick which {cap === 1 ? 'one stays' : `${cap} stay`} active — the rest will be locked until you upgrade or swap your selection.
        </p>

        <div style={{ overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
          {outlets.map(o => {
            const isSelected = selected.has(o.id);
            const disabled = !isSelected && atCap;
            return (
              <button
                key={o.id}
                onClick={() => toggle(o.id)}
                disabled={disabled}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                  borderRadius: 10, border: `1px solid ${isSelected ? '#6366f1' : '#e2e8f0'}`,
                  background: isSelected ? '#eef2ff' : '#fff',
                  opacity: disabled ? 0.4 : 1,
                  cursor: disabled ? 'default' : 'pointer',
                  textAlign: 'left', width: '100%',
                }}
              >
                <div style={{
                  width: 18, height: 18, borderRadius: 5, border: '1.5px solid #cbd5e1',
                  background: isSelected ? '#6366f1' : '#fff',
                  borderColor: isSelected ? '#6366f1' : '#cbd5e1',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  {isSelected && <span style={{ color: '#fff', fontSize: 11, fontWeight: 800 }}>✓</span>}
                </div>
                <span style={{ fontSize: 13, fontWeight: isSelected ? 700 : 600, color: isSelected ? '#4338ca' : '#334155', flex: 1 }}>
                  {o.name}
                </span>
                {!isSelected && !disabled && (
                  <span style={{ fontSize: 10, color: '#dc2626', fontWeight: 600 }}>currently locked</span>
                )}
              </button>
            );
          })}
        </div>

        <div style={{ textAlign: 'center', fontSize: 11, fontWeight: 700, color: '#94a3b8', marginBottom: 8 }}>
          {selected.size} / {cap} selected
        </div>

        {error && (
          <div style={{ fontSize: 11, color: '#dc2626', fontWeight: 600, textAlign: 'center', marginBottom: 10 }}>
            {error}
          </div>
        )}

        <button
          onClick={handleConfirm}
          disabled={saving || selected.size === 0}
          style={{
            width: '100%', padding: 12, borderRadius: 10, border: 'none',
            background: saving || selected.size === 0 ? '#e2e8f0' : '#6366f1',
            color: saving || selected.size === 0 ? '#94a3b8' : '#fff',
            fontWeight: 800, fontSize: 13, cursor: saving || selected.size === 0 ? 'default' : 'pointer',
          }}
        >
          {saving ? 'Saving...' : 'Confirm selection'}
        </button>
      </div>
    </div>
  );
}
